// Recipes — list view (cards / list toggle) + tag filter chips + New
// button. Tags come from the server's auto-classifier (background LLM
// call after import/create). Filter chips are split into Time/Effort
// and Cuisine/Dish rows so the user can stack constraints
// ("quick + italian"). Clicking a card opens the RecipeDetail panel.

import { useEffect, useMemo, useState } from "preact/hooks";
import "../../styles/recipes.css";

import { Icon } from "../../icons";
import { hueGrad } from "../../icons";
import { useRecipes, hueFromId } from "../../lib/use-recipes";
import { useRecipeTags } from "../../lib/use-recipe-tags";
import { type Recipe } from "../../lib/rpc";
import { RecipeDetail } from "./recipe-detail";
import { CookMode } from "./cook-mode";
import { NewRecipeModal } from "./new-recipe";
import { RecipeCatalogue } from "./recipe-catalogue";
import { ManageRecipeTagsSheet } from "./manage-recipe-tags";
import { RecipeSuggestions } from "./recipe-suggestions";
import { confirm } from "../../components/confirm";
import type { NotifyFn } from "../../components/toast";

type View = "cards" | "list";

const VIEW_KEY = "hub.recipes.view";

interface RecipesProps {
  notify: NotifyFn;
  /** Deep-link in: when set on mount or change, the section opens
   *  this recipe's detail panel. Used by the meal-plan "open recipe"
   *  cross-section nav. Parent clears via onOpenRecipeConsumed. */
  openRecipeId?: string | number | null;
  onOpenRecipeConsumed?: () => void;
  /** User's "feed N people" preference. Drives auto-scale on the
   *  recipe detail panel + cook mode together with recipe.serves
   *  (initialScale = household / recipe.serves). Null = preference
   *  not set; chips default to 1x. */
  householdSize?: number | null;
}

export function Recipes({ notify, openRecipeId = null, onOpenRecipeConsumed, householdSize = null }: RecipesProps) {
  const r = useRecipes();
  const recipeTags = useRecipeTags();
  const [manageTagsOpen, setManageTagsOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [view, setView] = useState<View>(() => {
    if (typeof window === "undefined") return "cards";
    const saved = localStorage.getItem(VIEW_KEY);
    if (saved === "list" || saved === "cards") return saved;
    return window.innerWidth < 760 ? "list" : "cards";
  });
  useEffect(() => { try { localStorage.setItem(VIEW_KEY, view); } catch {} }, [view]);

  // Set of selected tags (AND semantics — recipe must carry every
  // selected tag to be visible). Empty set = show all.
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [activeId, setActiveId] = useState<string | null>(null);
  const [cookId, setCookId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [catalogueOpen, setCatalogueOpen] = useState(false);

  // Deep-link in from meal plan: when openRecipeId changes to a
  // non-null value, open that recipe's detail panel and tell the
  // parent we've consumed it. Setting null doesn't re-trigger
  // anything, so a stale value can't accidentally re-pop the panel
  // after the user closes it.
  useEffect(() => {
    if (openRecipeId == null) return;
    setActiveId(String(openRecipeId));
    onOpenRecipeConsumed?.();
  }, [openRecipeId, onOpenRecipeConsumed]);
  // When set, the NewRecipeModal opens in edit mode for this recipe.
  // Detail panel's Edit button sets this; modal close clears it.
  const [editId, setEditId] = useState<string | null>(null);
  // Per-recipe scale factor (1x default). Lifted to the parent so the
  // value survives the detail → cook-mode transition (the panel
  // unmounts but the recipe id sticks around as cookId). Resets when
  // the user leaves the recipe entirely (closes detail without
  // cooking). Stored as a record so multiple recipes don't share
  // state if the user bounces around the list.
  const [scaleByRecipe, setScaleByRecipe] = useState<Record<string, number>>({});
  const getScale = (id: string) => scaleByRecipe[id] ?? 1;
  const setScale = (id: string, s: number) => setScaleByRecipe((prev) => ({ ...prev, [id]: s }));

  const toggleTag = (t: string) => {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t); else next.add(t);
      return next;
    });
  };

  // The effort row shows the user's full effort taxonomy (predictable
  // location for "weeknight" / "quick" etc., even when no recipe yet
  // carries the tag). The cuisine + dish row only shows tags that at
  // least one recipe carries — hides the long tail of cuisines the
  // household doesn't cook.
  const effortTags = useMemo(() => recipeTags.byCategory.effort.map((t) => t.name), [recipeTags.byCategory.effort]);
  const usedCategoryTags = useMemo(() => {
    const orderIndex = new Map<string, number>();
    [...recipeTags.byCategory.cuisine, ...recipeTags.byCategory.dish].forEach((t, i) => orderIndex.set(t.name, i));
    const seen = new Set<string>();
    for (const rec of r.recipes) {
      for (const t of rec.tags || []) if (orderIndex.has(t)) seen.add(t);
    }
    return [...seen].sort((a, b) => (orderIndex.get(a) ?? 999) - (orderIndex.get(b) ?? 999));
  }, [r.recipes, recipeTags.byCategory.cuisine, recipeTags.byCategory.dish]);

  const visible = selectedTags.size === 0
    ? r.recipes
    : r.recipes.filter((rec) => {
        const t = rec.tags || [];
        for (const sel of selectedTags) if (!t.includes(sel)) return false;
        return true;
      });

  // The full 3-stage New Recipe modal handles both URL import and
  // scratch creation; it calls back with the new id which we open in
  // the detail panel so the user sees the result immediately.
  const onNew = () => setNewOpen(true);

  return (
    <div className="recipes-main">
      <div className="recipes-toolbar">
        <div className="recipes-filter-group">
          <button
            className="chip recipes-all-chip"
            data-active={selectedTags.size === 0}
            onClick={() => setSelectedTags(new Set())}
            type="button"
          >All</button>
          <button
            className="recipes-filter-btn"
            data-active={selectedTags.size > 0}
            onClick={() => setFilterOpen(true)}
            type="button"
            aria-label="Open tag filter"
          >
            <Icon name="filter" size={12} />
            <span>Filter</span>
            {selectedTags.size > 0 && <span className="recipes-filter-badge">{selectedTags.size}</span>}
          </button>
          {selectedTags.size > 0 && (
            <div className="recipes-active-tags">
              {[...selectedTags].map((t) => (
                <button key={t} className="recipes-active-tag" type="button" onClick={() => toggleTag(t)} title="Remove">
                  #{t}
                  <Icon name="x" size={10} />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="recipes-toolbar-actions">
          <button className="recipes-manage-tags" type="button" onClick={() => setManageTagsOpen(true)} title="Manage tags">
            <Icon name="edit" size={11} />
            <span>Tags</span>
          </button>
          <div className="recipes-view-toggle">
            <button data-active={view === "cards"} onClick={() => setView("cards")} title="Cards" type="button">
              <Icon name="gallery" size={14} />
            </button>
            <button data-active={view === "list"} onClick={() => setView("list")} title="List" type="button">
              <Icon name="compact" size={14} />
            </button>
          </div>
          <button className="btn" onClick={() => setCatalogueOpen(true)} type="button" title="Browse the global recipe catalogue">
            <Icon name="search" size={14} />
            <span>Browse</span>
          </button>
          <button className="btn btn-primary" onClick={onNew} type="button">
            <Icon name="plus" size={14} />
            <span>New</span>
          </button>
        </div>
      </div>

      {filterOpen && (
        <RecipeFilterSheet
          effortTags={effortTags}
          usedCategoryTags={usedCategoryTags}
          allCuisineTags={recipeTags.byCategory.cuisine.map((t) => t.name)}
          allDishTags={recipeTags.byCategory.dish.map((t) => t.name)}
          selected={selectedTags}
          onToggle={toggleTag}
          onClear={() => setSelectedTags(new Set())}
          onClose={() => setFilterOpen(false)}
        />
      )}

      <RecipeSuggestions
        notify={notify}
        onOpenRecipe={(id) => setActiveId(id)}
      />

      {r.loading && (
        <p style={{ color: "var(--text-dim)", fontFamily: "var(--f-mono)", fontSize: 12 }}>loading…</p>
      )}

      {!r.loading && visible.length === 0 && (
        <div className="recipes-empty">
          <Icon name="flame" size={36} />
          <div className="recipes-empty-line">
            {selectedTags.size > 0
              ? "No recipes match those filters."
              : "No recipes yet. Tap Browse to pull from the catalogue, or New to add your own."}
          </div>
        </div>
      )}

      {!r.loading && visible.length > 0 && view === "cards" && (
        <div className="recipes-cards">
          {visible.map((recipe) => (
            <RecipeCard key={recipe.id} recipe={recipe} onOpen={() => setActiveId(recipe.id)} />
          ))}
        </div>
      )}

      {!r.loading && visible.length > 0 && view === "list" && (
        <div className="recipes-list">
          {visible.map((recipe) => (
            <RecipeListRow key={recipe.id} recipe={recipe} onOpen={() => setActiveId(recipe.id)} />
          ))}
        </div>
      )}

      <RecipeDetail
        recipeId={activeId}
        scale={activeId ? getScale(activeId) : 1}
        onScaleChange={(s) => { if (activeId) setScale(activeId, s); }}
        householdSize={householdSize}
        onClose={() => {
          // Reset scale when fully leaving the recipe (not just
          // transitioning into cook mode) so the next open starts at 1x.
          if (activeId) setScaleByRecipe((prev) => {
            const next = { ...prev }; delete next[activeId]; return next;
          });
          setActiveId(null);
        }}
        onCook={(id) => { setActiveId(null); setCookId(id); }}
        onEdit={(id) => setEditId(id)}
        onDelete={async (id) => {
          const ok = await confirm({
            title: "Delete this recipe?",
            message: "The recipe and all its ingredients/steps will be removed. This can't be undone.",
            confirmLabel: "Delete recipe",
            danger: true,
          });
          if (!ok) return;
          await r.remove(id);
          setActiveId(null);
          notify("Recipe deleted");
        }}
        onUpdate={r.update}
        onSetTags={r.setTags}
        onRetag={r.retag}
        notify={notify}
      />

      {cookId && (
        <CookMode
          recipeId={cookId}
          scale={getScale(cookId)}
          onScaleChange={(s) => setScale(cookId, s)}
          householdSize={householdSize}
          notify={notify}
          onClose={() => {
            setScaleByRecipe((prev) => {
              const next = { ...prev }; delete next[cookId]; return next;
            });
            setCookId(null);
          }} />
      )}

      <NewRecipeModal
        open={newOpen || editId !== null}
        editRecipeId={editId}
        onClose={() => { setNewOpen(false); setEditId(null); }}
        onCreated={async (id) => {
          await r.refresh();
          setActiveId(id);
        }}
        notify={notify}
      />

      {manageTagsOpen && (
        <ManageRecipeTagsSheet
          tags={recipeTags}
          notify={notify}
          onClose={() => setManageTagsOpen(false)}
        />
      )}

      <RecipeCatalogue
        open={catalogueOpen}
        onClose={() => setCatalogueOpen(false)}
        notify={notify}
        onImported={() => { void r.refresh(); }}
      />
    </div>
  );
}

function RecipeCard({ recipe, onOpen }: { recipe: Recipe; onOpen: () => void }) {
  const stepCount = Array.isArray(recipe.instructions) ? recipe.instructions.length : 0;
  const tags = (recipe.tags || []).slice(0, 3);
  return (
    <button className="recipe-card" onClick={onOpen} type="button">
      <div
        className="recipe-card-cover"
        style={recipe.imageUrl ? undefined : { background: hueGrad(hueFromId(recipe.id)) }}
      >
        {recipe.imageUrl && <img src={recipe.imageUrl} alt={recipe.name} />}
      </div>
      <div className="recipe-card-meta">
        <div className="recipe-card-title">{recipe.name}</div>
        <div className="recipe-card-sub">
          {stepCount > 0 ? `${stepCount} step${stepCount === 1 ? "" : "s"}` : "no steps yet"}
        </div>
        {tags.length > 0 && (
          <div className="recipe-card-tags">
            {tags.map((t) => <span key={t} className="recipe-card-tag">#{t}</span>)}
          </div>
        )}
      </div>
    </button>
  );
}

function RecipeListRow({ recipe, onOpen }: { recipe: Recipe; onOpen: () => void }) {
  const stepCount = Array.isArray(recipe.instructions) ? recipe.instructions.length : 0;
  const tags = (recipe.tags || []).slice(0, 4);
  return (
    <div className="recipe-list-row" onClick={onOpen}>
      <div
        className="recipe-list-thumb"
        style={recipe.imageUrl ? undefined : { background: hueGrad(hueFromId(recipe.id)) }}
      >
        {recipe.imageUrl && <img src={recipe.imageUrl} alt={recipe.name} />}
      </div>
      <div className="recipe-list-meta">
        <div className="recipe-list-title">{recipe.name}</div>
        <div className="recipe-list-sub">
          {stepCount > 0 ? `${stepCount} step${stepCount === 1 ? "" : "s"}` : "no steps yet"}
          {tags.length > 0 && (
            <span className="recipe-list-tags">
              {tags.map((t) => <span key={t} className="recipe-list-tag"> · #{t}</span>)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

interface RecipeFilterSheetProps {
  effortTags: string[];
  // Tags in cuisine + dish that at least one current recipe carries.
  // Showed first in their respective sections as "popular" anchors so the
  // user finds them without scrolling past unused alternatives.
  usedCategoryTags: string[];
  allCuisineTags: string[];
  allDishTags: string[];
  selected: Set<string>;
  onToggle: (t: string) => void;
  onClear: () => void;
  onClose: () => void;
}

function RecipeFilterSheet({ effortTags, usedCategoryTags, allCuisineTags, allDishTags, selected, onToggle, onClear, onClose }: RecipeFilterSheetProps) {
  // Promote tags that are currently carried by some recipe so they
  // sort to the top of each cuisine/dish section. Users scroll less to
  // hit the chip they're likely to filter on. Effort tags don't get
  // re-sorted — that row is fixed-shape and predictable.
  const usedSet = new Set(usedCategoryTags);
  const sortByUsed = (a: string, b: string) => {
    const au = usedSet.has(a), bu = usedSet.has(b);
    if (au && !bu) return -1;
    if (!au && bu) return 1;
    return 0;
  };
  const cuisineSorted = allCuisineTags.slice().sort(sortByUsed);
  const dishSorted = allDishTags.slice().sort(sortByUsed);

  const renderChip = (t: string) => (
    <button
      key={t}
      className="chip"
      data-active={selected.has(t)}
      onClick={() => onToggle(t)}
      type="button"
    >#{t}</button>
  );

  return (
    <div className="confirm-bg" onClick={onClose}>
      <div className="recipes-filter-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Filter recipes">
        <header className="recipes-filter-head">
          <h3>Filter recipes</h3>
          <button className="icon-btn" onClick={onClose} type="button" aria-label="Close" title="Close">
            <Icon name="x" size={13} />
          </button>
        </header>

        <div className="recipes-filter-scroll">
          <section className="recipes-filter-section">
            <div className="recipes-filter-section-label">Time / effort</div>
            <div className="recipes-filter-chips">
              {effortTags.length > 0
                ? effortTags.map(renderChip)
                : <div className="recipes-filter-empty">No effort tags configured.</div>}
            </div>
          </section>

          <section className="recipes-filter-section">
            <div className="recipes-filter-section-label">Cuisine</div>
            <div className="recipes-filter-chips">
              {cuisineSorted.length > 0
                ? cuisineSorted.map(renderChip)
                : <div className="recipes-filter-empty">No cuisine tags configured.</div>}
            </div>
          </section>

          <section className="recipes-filter-section">
            <div className="recipes-filter-section-label">Dish</div>
            <div className="recipes-filter-chips">
              {dishSorted.length > 0
                ? dishSorted.map(renderChip)
                : <div className="recipes-filter-empty">No dish tags configured.</div>}
            </div>
          </section>
        </div>

        <footer className="recipes-filter-foot">
          <button className="recipes-filter-clear" type="button" onClick={onClear} disabled={selected.size === 0}>
            Clear ({selected.size})
          </button>
          <button className="manage-tags-done" type="button" onClick={onClose}>Done</button>
        </footer>
      </div>
    </div>
  );
}
